import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";

import { useAuth } from "../../contexts/AuthContext";
import { getAccessToken } from "../../awsAuth";

import DynamicHeader from "../Header/DynamicHeader";
import ParticipantsList from "./ParticipantsList";
import CommunityActions from "./CommunityActions";
import CommunityBlockedModal from "./CommunityBlockedModal";

import CommunityFeed from "./CommunityFeed";

import PhotoDetails from "../PhotoDetails/PhotoDetails";

import {
  UI_TTL,
  HARD_TTL,
  cacheGetWithAge,
  cacheSet,
} from "../../utils/swrcache";

import "./CommunityPage.scss";

const DEFAULT_AVATAR =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="240" height="240" viewBox="0 0 240 240">
      <rect width="240" height="240" rx="120" fill="#eef2f7"/>
      <circle cx="120" cy="92" r="38" fill="#cbd5e1"/>
      <path d="M55 198c12-34 39-56 65-56s53 22 65 56" fill="#cbd5e1"/>
    </svg>
  `);

const isCanceledRequest = (e) => {
  return (
    e?.name === "AbortError" ||
    e?.name === "CanceledError" ||
    e?.code === "ERR_CANCELED" ||
    axios.isCancel?.(e)
  );
};

const CommunityPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();

  const [community, setCommunity] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [members, setMembers] = useState([]);
  const [creatorData, setCreatorData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isBlockedByCommunity, setIsBlockedByCommunity] = useState(false);

  const [isAvatarOpen, setIsAvatarOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isParticipantsOpen, setIsParticipantsOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  const [pendingRequest, setPendingRequest] = useState(false);

  const [requestsCount, setRequestsCount] = useState(0);

  const [notifEnabled, setNotifEnabled] = useState(true);

  const isPrivate = !!community?.isPrivate;

  const canSeeContent = useMemo(() => {
    if (!community) return false;
    if (isBlockedByCommunity) return false;
    return !isPrivate || isCreator || isMember;
  }, [community, isPrivate, isCreator, isMember, isBlockedByCommunity]);

  const canPost = useMemo(() => {
    if (!community) return false;
    if (isBlockedByCommunity) return false;
    if ((community.mode || "community") === "channel") {
      return isCreator;
    }
    return !isPrivate || isCreator || isMember;
  }, [community, isPrivate, isCreator, isMember, isBlockedByCommunity]);

  const commentsEnabled = community?.allowComments !== false;

  const authHeaders = async () => {
    const token = await getAccessToken();
    if (!token) throw new Error("Немає токена авторизації");
    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const authGet = async (url, config = {}) => {
    const headers = await authHeaders();
    return axios.get(url, {
      ...config,
      headers: {
        ...(config.headers || {}),
        ...headers,
      },
    });
  };

  const authPost = async (url, data = {}, config = {}) => {
    const headers = await authHeaders();
    return axios.post(url, data, {
      ...config,
      headers: {
        ...(config.headers || {}),
        ...headers,
      },
    });
  };

  const authPatch = async (url, data = {}, config = {}) => {
    const headers = await authHeaders();
    return axios.patch(url, data, {
      ...config,
      headers: {
        ...(config.headers || {}),
        ...headers,
      },
    });
  };

  const authDelete = async (url, config = {}) => {
    const headers = await authHeaders();
    return axios.delete(url, {
      ...config,
      headers: {
        ...(config.headers || {}),
        ...headers,
      },
    });
  };

  /* broadcast лічильника в Communities */
  const broadcastMembersCount = React.useCallback(
    (count) => {
      if (!id || typeof count !== "number") return;
      try {
        window.dispatchEvent(
          new CustomEvent("cv:communityMembersChanged", {
            detail: { communityId: id, totalMembers: count },
          })
        );
      } catch {}
    },
    [id]
  );

  useEffect(() => {
    const checkBlock = async () => {
      if (!id || !userId) {
        setIsBlockedByCommunity(false);
        return;
      }
      try {
        const res = await authGet(`/api/block/community/${id}/check`);
        setIsBlockedByCommunity(!!res.data?.isBlocked);
      } catch {
        setIsBlockedByCommunity(false);
      }
    };
    checkBlock();
  }, [id, userId]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const CK_INFO = `community:info:${id}`;
    const CK_MEMBERS = `community:members:${id}`;
    const CK_CREATOR = `community:creator:${id}`;

    theCache: {
      const infoCached = cacheGetWithAge(CK_INFO, HARD_TTL);
      const memCached = cacheGetWithAge(CK_MEMBERS, HARD_TTL);
      const creCached = cacheGetWithAge(CK_CREATOR, HARD_TTL);

      if (infoCached && !cancelled) {
        setCommunity(infoCached.data);
        if (infoCached.data?.creatorId === userId) {
          setIsCreator(true);
          setIsMember(true);
        }
        setLoading(false);
      }

      if (memCached && !cancelled) {
        const list = Array.isArray(memCached.data) ? memCached.data : [];
        setMembers(list);
        if (list.find((m) => m.userId === userId)) setIsMember(true);
      }

      if (creCached && !cancelled) setCreatorData(creCached.data);
    }

    const infoCtrl = new AbortController();
    const memCtrl = new AbortController();
    const creCtrl = new AbortController();

    const fetchCreatorFresh = async (creatorId) => {
      let timeoutId;
      try {
        timeoutId = setTimeout(() => creCtrl.abort(), 6000);

        const res = await axios.get(`/api/account/${creatorId}`, {
          signal: creCtrl.signal,
        });

        if (cancelled) return;

        const data = res.data;
        setCreatorData(data);
        cacheSet(CK_CREATOR, data);
      } catch (e) {
        if (!isCanceledRequest(e)) console.error("❌ creator:", e);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    const fetchInfoFresh = async () => {
      let timeoutId;
      try {
        timeoutId = setTimeout(() => infoCtrl.abort(), 6000);

        const res = await axios.get(`/api/communities/${id}`, {
          signal: infoCtrl.signal,
        });

        const data = res.data;
        if (cancelled) return;

        setCommunity(data);
        cacheSet(CK_INFO, data);

        if (data?.creatorId === userId) {
          setIsCreator(true);
          setIsMember(true);
        } else {
          setIsCreator(false);
        }

        if (data?.creatorId) fetchCreatorFresh(data.creatorId);

        if (!cacheGetWithAge(CK_INFO, HARD_TTL) && !cancelled) {
          setLoading(false);
        }

        if (userId && data?.creatorId === userId) {
          try {
            const cntRes = await authGet(`/api/communities/${id}/requests/count`);
            const cntJson = cntRes.data || { count: 0 };
            setRequestsCount(Number(cntJson.count || 0));
          } catch (e) {
            console.error("❌ requests count:", e);
          }
        }
      } catch (e) {
        if (!isCanceledRequest(e)) console.error("❌ community info:", e);
        if (!cacheGetWithAge(CK_INFO, HARD_TTL) && !cancelled) {
          setLoading(false);
        }
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    const fetchMembersFresh = async () => {
      let timeoutId;
      try {
        timeoutId = setTimeout(() => memCtrl.abort(), 6000);

        const res = await axios.get(`/api/communities/${id}/members`, {
          signal: memCtrl.signal,
        });

        const data = res.data;
        if (cancelled) return;

        const list = Array.isArray(data) ? data : [];
        setMembers(list);
        cacheSet(CK_MEMBERS, list);
        setIsMember(!!list.find((m) => m.userId === userId));

        broadcastMembersCount(list.length);
      } catch (e) {
        if (!isCanceledRequest(e)) console.error("❌ members:", e);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    const schedule = (cached, fn) => {
      if (!cached) fn();
      else if (cached.ageMs <= UI_TTL) {
        if ("requestIdleCallback" in window) {
          window.requestIdleCallback(fn, { timeout: 2000 });
        } else {
          setTimeout(fn, 0);
        }
      } else {
        fn();
      }
    };

    const infoCached = cacheGetWithAge(`community:info:${id}`, HARD_TTL);
    const memCached = cacheGetWithAge(`community:members:${id}`, HARD_TTL);
    const creCached = cacheGetWithAge(`community:creator:${id}`, HARD_TTL);

    schedule(infoCached, fetchInfoFresh);
    schedule(memCached, fetchMembersFresh);

    if (community?.creatorId) {
      schedule(creCached, () => fetchCreatorFresh(community.creatorId));
    }

    return () => {
      cancelled = true;
      infoCtrl.abort();
      memCtrl.abort();
      creCtrl.abort();
    };
  }, [id, userId]);

  useEffect(() => {
    const loadStatus = async () => {
      if (!id || !userId) return;
      try {
        const res = await authGet(`/api/communities/${id}/requests/status`);
        const json = res.data;
        setPendingRequest(!!json.pending);
        if (json.isMember) setIsMember(true);
      } catch {}
    };
    loadStatus();
  }, [id, userId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id || !userId) {
        setNotifEnabled(true);
        return;
      }

      try {
        const r = await authGet(`/api/communities/subscriptions`);
        const data = r.data;
        const rec = (data?.items || []).find(
          (it) => String(it.communityId) === String(id)
        );
        if (!cancelled) setNotifEnabled(rec ? !!rec.enabled : true);
      } catch (e) {
        console.error("❌ subscriptions:", e);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, userId]);

  useEffect(() => {
    const onExternalToggle = (e) => {
      const { communityId, enabled } = e?.detail || {};
      if (String(communityId) === String(id)) {
        setNotifEnabled(!!enabled);
      }
    };
    window.addEventListener("cv:communityNotifToggled", onExternalToggle);
    return () =>
      window.removeEventListener("cv:communityNotifToggled", onExternalToggle);
  }, [id]);

  const handleJoin = async () => {
    try {
      if (isBlockedByCommunity) return;

      if (isPrivate) {
        if (pendingRequest) {
          await authDelete(`/api/communities/${id}/request-join`);
          setPendingRequest(false);
          return;
        }

        await authPost(`/api/communities/${id}/request-join`, {});
        setPendingRequest(true);
        return;
      }

      await authPost(`/api/communities/${id}/join`, {});

      setIsMember(true);

      try {
        window.dispatchEvent(
          new CustomEvent("cv:communityMembershipChanged", {
            detail: {
              action: "joined",
              communityId: String(id),
              userId: String(userId),
            },
          })
        );
      } catch {}

      setMembers((prev) => {
        if (prev.some((m) => m.userId === userId)) return prev;

        const next = [...prev, { userId, role: "member" }];
        const count = next.length;

        cacheSet(`community:members:${id}`, next);

        setCommunity((prevComm) => {
          if (!prevComm) return prevComm;
          const updated = { ...prevComm, membersCount: count };
          cacheSet(`community:info:${id}`, updated);
          return updated;
        });

        broadcastMembersCount(count);

        return next;
      });
    } catch (err) {
      console.error(err);
      alert("Не вдалося приєднатися");
    }
  };

  const handleLeave = async () => {
    try {
      await authPost(`/api/communities/${id}/leave`, {});
      setIsMember(false);

      setMembers((prev) => {
        const next = prev.filter((m) => m.userId !== userId);
        const count = next.length;

        cacheSet(`community:members:${id}`, next);

        setCommunity((prevComm) => {
          if (!prevComm) return prevComm;
          const updated = {
            ...prevComm,
            membersCount: count,
          };
          cacheSet(`community:info:${id}`, updated);
          return updated;
        });

        broadcastMembersCount(count);

        return next;
      });
    } catch (err) {
      console.error(err);
    }
  };

  const goToAuthorProfile = () => {
    if (community?.creatorId) navigate(`/profile/${community.creatorId}`);
  };

  const updateCommunitySettings = async (patch) => {
    if (!community || !userId) return;

    const payload = {
      name: community.name || "",
      description: community.description || "",
      avatar: community.avatar || "",
      location: community.location || "",
      showLocation: !!community.showLocation,
      ...patch,
    };

    try {
      await authPatch(`/api/communities/${encodeURIComponent(id)}`, payload);
    } catch (e) {
      const msg =
        e.response?.data?.error ||
        e.response?.data?.message ||
        e.message ||
        "PATCH failed";
      throw new Error(`PATCH failed: ${msg}`);
    }

    setCommunity((prev) => {
      const next = { ...(prev || {}), ...patch };
      cacheSet(`community:info:${id}`, next);
      return next;
    });
  };

  const onTogglePrivacy = async (value) => {
    try {
      await updateCommunitySettings({ isPrivate: !!value });
    } catch (e) {
      console.error("toggle privacy:", e);
      alert("Не вдалося змінити тип спільноти.");
    }
  };

  const onToggleMode = async (newMode) => {
    try {
      await updateCommunitySettings({ mode: newMode });
    } catch (e) {
      console.error("toggle mode:", e);
      alert("Не вдалося змінити режим.");
    }
  };

  const onToggleComments = async (value) => {
    try {
      await updateCommunitySettings({ allowComments: !!value });
    } catch (e) {
      console.error("toggle comments:", e);
      alert("Не вдалося змінити налаштування коментарів.");
    }
  };

  const toggleNotificationsHere = async () => {
    const next = !notifEnabled;
    setNotifEnabled(next);

    try {
      await authPost(`/api/communities/${id}/notifications`, {
        enabled: next,
      });
    } finally {
      try {
        window.dispatchEvent(
          new CustomEvent("cv:communityNotifToggled", {
            detail: { communityId: id, enabled: next },
          })
        );
      } catch {}
    }
  };

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);

  const openPhotoViewer = (photos = [], startIndex = 0) => {
    const normalized = (Array.isArray(photos) ? photos : [])
      .map((p) => {
        if (!p) return null;
        if (typeof p === "string") return { src: p, alt: "" };
        if (p.url || p.src) return { src: p.url || p.src, alt: p.alt || "" };
        return null;
      })
      .filter(Boolean);

    if (!normalized.length) return;
    setViewerPhotos(normalized);
    setViewerIndex(
      Math.max(0, Math.min(startIndex || 0, normalized.length - 1))
    );
    setViewerOpen(true);
  };

  useEffect(() => {
    const handler = (e) => {
      const { photos, startIndex } = e?.detail || {};
      openPhotoViewer(photos, startIndex || 0);
    };
    window.addEventListener("cv:openPhotoViewer", handler);
    return () => window.removeEventListener("cv:openPhotoViewer", handler);
  }, []);

  if (loading && !community) {
    return (
      <div className="community-page">
        <DynamicHeader
          title="Спільнота"
          variant="glass"
          size="compact"
          hideOnScroll={true}
          showBack={true}
          showMenu={false}
          withSpacer={true}
        />
        <div className="community-loading">
          <div className="skeleton-header">
            <div className="skeleton-avatar" />
            <div className="skeleton-lines">
              <div className="skeleton-line w40" />
              <div className="skeleton-line w60" />
              <div className="skeleton-line w30" />
            </div>
          </div>
          <div className="skeleton-members">
            <div className="skeleton-line w30" />
            <div className="skeleton-avatars">
              <div className="skeleton-circle" />
              <div className="skeleton-circle" />
              <div className="skeleton-circle" />
            </div>
          </div>
          <div className="skeleton-description">
            <div className="skeleton-line w80" />
            <div className="skeleton-line w90" />
            <div className="skeleton-line w70" />
          </div>
        </div>
      </div>
    );
  }

  if (!community) return <p className="community-error">Спільноту не знайдено</p>;

  const visibleMembers = members.slice(0, 3);
  const extraCount = members.length > 3 ? members.length - 3 : 0;

  const joinBtnText = isCreator
    ? "Редагувати"
    : isMember
    ? "Вийти"
    : isPrivate
    ? pendingRequest
      ? "Заявку надіслано"
      : "Надіслати заявку"
    : "Приєднатись";

  // SEO: this page never carried any title/description/canonical before
  // this audit — every community rendered under the generic "Cvanto" title
  // from public/index.html, which is exactly the "duplicate content, no
  // user-selected canonical" pattern the owner is seeing in Search Console
  // for communities today. Private communities are marked noindex — their
  // content isn't visible to a non-member anyway, so indexing just the join
  // shell would be a low-value page, not a useful landing page.
  const seoUrl = `https://cvanto.com/communities/${encodeURIComponent(id)}`;
  const seoTitle = `${community.name || "Спільнота"} — Cvanto`;
  const seoDescRaw = String(community.description || "").replace(/\s+/g, " ").trim();
  const seoDesc = (seoDescRaw || `Спільнота «${community.name || ""}» на Cvanto.`).slice(0, 160);

  return (
    <div className="community-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={seoUrl} />
        {isPrivate ? <meta name="robots" content="noindex,follow" /> : null}

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cvanto" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={community.avatar || DEFAULT_AVATAR} />
      </Helmet>

      <DynamicHeader
        title={community?.name || "Спільнота"}
        variant="glass"
        size="compact"
        hideOnScroll={true}
        showBack={true}
        showMenu={true}
        onMenuClick={() => setShowActions(true)}
        withSpacer={true}
      />

      <div className="community-info">
        <img
          src={community.avatar || DEFAULT_AVATAR}
          alt={community.name}
          className="community-avatar"
          onClick={() => setIsAvatarOpen(true)}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = DEFAULT_AVATAR;
          }}
        />

        <div className="community-main">
          <span className="community-date">
            {community.createdAt
              ? new Date(community.createdAt).toLocaleDateString("uk-UA")
              : ""}
          </span>
          <h1 title={community.name}>{community.name}</h1>

          {creatorData && (
            <div
              className="community-author"
              onClick={goToAuthorProfile}
              style={{ cursor: "pointer" }}
              title={`Автор: ${creatorData.username}`}
            >
              <img
                src={creatorData.avatar || DEFAULT_AVATAR}
                alt="author"
                className="author-avatar"
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_AVATAR;
                }}
              />
              <span className="author-name">Автор: {creatorData.username}</span>
            </div>
          )}

          {community.location && !!community.showLocation && (
            <p className="community-location">{community.location}</p>
          )}
        </div>
      </div>

      {isAvatarOpen && (
        <div
          className={`avatar-modal ${isClosing ? "closing" : ""}`}
          onClick={() => {
            setIsClosing(true);
            setTimeout(() => {
              setIsAvatarOpen(false);
              setIsClosing(false);
            }, 300);
          }}
        >
          <img
            src={community.avatar || DEFAULT_AVATAR}
            alt="Community Avatar enlarged"
            className="avatar-modal__image"
            onClick={(e) => e.stopPropagation()}
            loading="eager"
            decoding="async"
          />
        </div>
      )}

      <div
        className="community-members"
        onClick={() => !isBlockedByCommunity && setIsParticipantsOpen(true)}
        style={{
          cursor: isBlockedByCommunity ? "not-allowed" : "pointer",
          opacity: isBlockedByCommunity ? 0.5 : 1,
        }}
        title={
          isBlockedByCommunity
            ? "Ви заблоковані у цій спільноті"
            : "Переглянути учасників"
        }
      >
        <p
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span>Учасники</span>
          <span
            style={{
              fontWeight: 400,
              fontSize: 13,
              color: "#6b7280",
            }}
          >
            · {members.length} учасників
          </span>
          {isCreator && requestsCount > 0 && (
            <span
              style={{
                background: "#ff3b30",
                color: "#fff",
                borderRadius: 999,
                padding: "0 8px",
                fontSize: 12,
                lineHeight: "18px",
                display: "inline-block",
                minWidth: 22,
                textAlign: "center",
              }}
            >
              +{requestsCount}
            </span>
          )}
        </p>
        <div className="members-avatars">
          {visibleMembers.map((m) => (
            <img
              key={m.userId}
              src={m.avatar || DEFAULT_AVATAR}
              alt="member"
              className="member-avatar"
              loading="lazy"
              decoding="async"
              width={32}
              height={32}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_AVATAR;
              }}
            />
          ))}
          {extraCount > 0 && <span className="members-count">+{extraCount}</span>}
        </div>
      </div>

      {community.description && (
        <div className="community-description">
          <p className="description-title">
            <strong>Опис</strong>
          </p>
          <p>{community.description}</p>
        </div>
      )}

      {!isBlockedByCommunity && (
        <div className="community-buttons">
          {isCreator ? (
            <button
              className="edit-btn"
              onClick={() => navigate(`/community/${id}/edit`)}
            >
              Редагувати
            </button>
          ) : isMember ? (
            <button className="leave-btn" onClick={handleLeave}>
              Вийти
            </button>
          ) : (
            <button
              className={
                isPrivate
                  ? pendingRequest
                    ? "leave-btn"
                    : "join-btn"
                  : "join-btn"
              }
              onClick={handleJoin}
            >
              {joinBtnText}
            </button>
          )}
        </div>
      )}

      {isBlockedByCommunity && (
        <div
          className="community-locked"
          style={{
            background: "#fff",
            border: "1px solid #eee",
            borderRadius: 14,
            padding: 16,
            margin: "12px 0",
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0 }}>
            🚫 Ви заблоковані у цій спільноті. Контент недоступний.
          </p>
        </div>
      )}

      {!isBlockedByCommunity && !canSeeContent && (
        <div
          className="community-locked"
          style={{
            background: "#fff",
            border: "1px solid #eee",
            borderRadius: 14,
            padding: 16,
            margin: "12px 0",
            textAlign: "center",
          }}
        >
          <p style={{ margin: 0 }}>
            🔒 Це закрита спільнота. Контент доступний лише учасникам.
          </p>
        </div>
      )}

      <CommunityFeed
        communityId={id}
        userId={userId}
        canSeeContent={canSeeContent}
        canPost={canPost}
        commentsEnabled={commentsEnabled}
        isBlockedByCommunity={isBlockedByCommunity}
        onOpenPhotoViewer={openPhotoViewer}
      />

      {isParticipantsOpen && (
        <ParticipantsList
          members={members}
          onClose={() => setIsParticipantsOpen(false)}
          isAdmin={isCreator}
          communityId={id}
          currentUserId={userId}
          onRequestsHandled={(delta) => {
            if (typeof delta === "number") {
              setRequestsCount((c) => Math.max(0, (c || 0) + delta));
            }
          }}
          onMembersUpdate={(next) => {
            const safe = Array.isArray(next) ? next : [];
            const count = safe.length;

            setMembers(safe);

            setCommunity((prev) => {
              if (!prev) return prev;
              const updated = { ...prev, membersCount: count };
              cacheSet(`community:info:${id}`, updated);
              return updated;
            });

            broadcastMembersCount(count);
          }}
        />
      )}

      {showActions && (
        <CommunityActions
          isOwner={isCreator}
          onClose={() => setShowActions(false)}
          isPrivate={!!community?.isPrivate}
          mode={community?.mode || "community"}
          allowComments={!!community?.allowComments}
          onTogglePrivacy={onTogglePrivacy}
          onToggleMode={onToggleMode}
          onToggleComments={onToggleComments}
          onEdit={() => navigate(`/community/${id}/edit`)}
          onShowBlocked={() => {
            setShowActions(false);
            setShowBlockedModal(true);
          }}
          onShare={() => {
            navigator.clipboard.writeText(window.location.href);
            setShowActions(false);
          }}
          communityId={id}
          userId={userId}
          notificationsEnabled={notifEnabled}
          onToggleNotifications={async () => {
            await toggleNotificationsHere();
            setShowActions(false);
          }}
          onReport={() => {
            console.log("🚩 report community");
            setShowActions(false);
          }}
          onLeave={() => {
            handleLeave();
            setShowActions(false);
          }}
          onDeleteCommunity={async () => {
            try {
              await authDelete(`/api/communities/${id}`);

              try {
                cacheSet(`community:info:${id}`, null);
                cacheSet(`community:members:${id}`, []);
                cacheSet(`community:posts:${id}`, []);
              } catch {}

              navigate("/communities");
            } catch (e) {
              console.error("[UI] DELETE community failed", e);
              throw e;
            }
          }}
        />
      )}

      {showBlockedModal && (
        <CommunityBlockedModal
          communityId={id}
          adminId={userId}
          onClose={() => setShowBlockedModal(false)}
        />
      )}

      <PhotoDetails
        isOpen={viewerOpen}
        photos={viewerPhotos}
        startIndex={viewerIndex}
        onClose={() => setViewerOpen(false)}
      />
    </div>
  );
};

export default CommunityPage;